import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { DialogActions, DialogContent } from "@mui/material";
import {
  Autocomplete,
  Button,
  CreatedUpdatedBy,
  DatePicker,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useExchangeRateForm, {
  UseExchangeRateFormProps,
  UseExchangeRateFormReturnType,
} from "./useExchangeRateForm";
export const ExchangeRateContext = createContext<
  UseExchangeRateFormReturnType | undefined
>(undefined);
export interface ExchangeRateFormProps extends UseExchangeRateFormProps {}

export default function ExchangeRateForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: ExchangeRateFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useExchangeRateForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate
    ? I18n.t("ExchangeRate.addTitle")
    : `${item?.date_from} - ${item?.rate}`;
  const subtitle = isCreate ? undefined : undefined;
  return (
    <ParamsContext.Provider value={{ exchange_rate_id: id }}>
      <ExchangeRateContext.Provider value={form}>
        <PageHeader
          avatar={<CurrencyExchangeIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="ExchangeRate.attributes"
            translationPlaceholdersCategory="ExchangeRate.placeholders"
            translationHelpersCategory="ExchangeRate.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("exchange_rate", item)}
          >
            <FieldsContainer>
              <Field fullWidth>
                <Autocomplete id="type" />
              </Field>
              <Field fullWidth>
                <TextField id="rate" />
              </Field>
              <Field fullWidth>
                <DatePicker id="date_from" />
              </Field>
              <Field fullWidth>
                <DatePicker id="date_to" />
              </Field>{" "}
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="ExchangeRate"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </ExchangeRateContext.Provider>
    </ParamsContext.Provider>
  );
}
